import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

const AXIS_TICK = { fill: "#7a736c", fontSize: 12 };
const GRID = "#e6dfd2";

/* Shared clean tooltip */
// eslint-disable-next-line
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-line bg-elevated px-3 py-2 shadow-lg">
      {label && <p className="mb-1 font-mono text-xs text-ink-3">{label}</p>}
      {payload.map((p: { name?: string; value?: number | string; payload?: { name?: string } }, i: number) => (
        <p key={i} className="text-sm font-semibold text-ink">
          {p.payload?.name ?? p.name}: <span className="font-mono">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

/* ① Simple vertical bars — calls per day */
export function CallsPerDayChart({ data }: { data: { day: string; calls: number }[] }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="day" tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: "#f0eee6" }} />
        <Bar dataKey="calls" name="Calls" fill="#f0530f" radius={[6, 6, 0, 0]} maxBarSize={36} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ② Donut with center total + plain legend */
export function OutcomeDonut({ data }: { data: { name: string; value: number; color: string }[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div>
      <div className="relative">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name"
              innerRadius={62} outerRadius={88} paddingAngle={2} strokeWidth={0}>
              {data.map((d) => <Cell key={d.name} fill={d.color} />)}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-mono text-2xl font-bold text-ink">{total.toLocaleString("en-IN")}</span>
          <span className="text-[10px] uppercase tracking-wider text-ink-3">Total calls</span>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5">
        {data.map((d) => (
          <li key={d.name} className="flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />
            <span className="flex-1 text-ink-2">{d.name}</span>
            <span className="font-mono font-semibold text-ink">
              {total > 0 ? ((d.value / total) * 100).toFixed(1) : "0.0"}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ③ Bars with % labels on top — pickup by time window (best window highlighted) */
export function HourlyPickupChart({ data }: {
  data: {
    window: string;
    pickup: number;
    attempted: number;
    connected: number;
    best?: boolean;
  }[];
}) {
  const PickupTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    return (
      <div className="rounded-lg border border-line bg-elevated px-3 py-2 shadow-lg">
        <p className="mb-1 font-mono text-xs text-ink-3">{label}</p>
        <p className="text-sm font-semibold text-ink">
          Pickup rate: <span className="font-mono">{row.pickup}%</span>
        </p>
        
      </div>
    );
  };

  const PickupDot = ({ cx, cy, index, value }: any) => {
    if (value === null || value === undefined) return null;
    const best = data[index]?.best;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={best ? 5 : 4}
        fill={best ? "#c43b00" : "#ffffff"}
        stroke="#f0530f"
        strokeWidth={2.5}
      />
    );
  };

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 24, right: 16, left: -12, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis
          dataKey="window"
          tick={{ ...AXIS_TICK, fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          interval={0}
          minTickGap={0}
          height={34}
          padding={{ left: 36, right: 36 }}
        />
        <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} unit="%" domain={[0, 100]} />
        <Tooltip content={<PickupTooltip />} cursor={{ stroke: "#d8d1c5", strokeDasharray: "4 4" }} />
        <Line
          type="linear"
          dataKey="pickup"
          name="Pickup rate"
          stroke="#f0530f"
          strokeWidth={3}
          connectNulls
          dot={<PickupDot />}
          activeDot={{ r: 6, fill: "#c43b00", stroke: "#ffffff", strokeWidth: 2 }}
        >
          <LabelList
            dataKey="pickup"
            position="top"
            formatter={(v: any) => `${v}%`}
            style={{ fill: "#7a736c", fontSize: 11, fontFamily: "JetBrains Mono" }}
          />
        </Line>
      </LineChart>
    </ResponsiveContainer>
  );
}

/* ④ Grouped bars — manual vs AI hours (the "hours saved" story) */
